import { useAuth } from "hooks/auth";
import React, { useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch } from "react-redux";
import { setLogoutReason } from "reduxState/logoutReason/logoutReasonSlice";
import { getEnvVariable } from "utilities/env";
import { IdleCheckerContext } from "./IdleCheckerContext";

type Props = { children: React.ReactNode };

export function IdleCheckerProvider({ children }: Props) {
  const { logout, hasToken } = useAuth();
  const dispatch = useDispatch();
  const timeToLogout = Number(getEnvVariable("REACT_APP_AUTOMATIC_LOGOUT"));
  const isValidIdleTime = !isNaN(timeToLogout) && timeToLogout !== 0;

  const onIdle = () => {
    dispatch(setLogoutReason("idle"));
    pause();
    logout();
  };

  const milisecond = 60000;
  const { start: startIdle, pause } = useIdleTimer({
    onIdle,
    crossTab: true,
    syncTimers: 500,
    // Time need to be changed to miliseconds
    timeout: isValidIdleTime ? timeToLogout * milisecond : 1,
    startManually: true,
    startOnMount: false,
  });

  const runIdleChecker = (loggedUser?: boolean) => {
    if (isValidIdleTime && (hasToken || loggedUser)) {
      return startIdle();
    }
  };

  useEffect(() => {
    // Run on refresh page
    runIdleChecker();
    // eslint-disable-next-line
  }, []);

  const clearLogoutReason = () => dispatch(setLogoutReason(""));

  useEffect(() => {
    // Remove logout reason after user refresh page
    if (isValidIdleTime) {
      window.addEventListener("beforeunload", clearLogoutReason);
    }
    return () => {
      window.removeEventListener("beforeunload", clearLogoutReason);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <IdleCheckerContext.Provider
      value={{
        stopIdleChecker: pause,
        runIdleChecker,
      }}
    >
      {children}
    </IdleCheckerContext.Provider>
  );
}
